"use strict"

import React, { useState, useEffect, useRef } from "react"
import propTypes from "prop-types"
import { Button, ButtonGroup, OverlayTrigger, Popover } from "react-bootstrap"

import { showModal, isNumber } from "../../../utils"
import GraphImageModal, { GraphImageLegend } from "../../../graphing/modals/GraphImageModal"
import DataAndCommentExportModals from "../../../graphing/modals/DataAndCommentExportModals"
import ExcelTravelerButton from "../../../graphing/modals/ExcelTravelerButton"
import DownloadPopoverMenu from "../../../graphing/plotting/widgets/DownloadPopoverMenu"
import ToggleAnnotationsButton from "../../../graphing/plotting/widgets/ToggleAnnotationsButton"
import ToggleRunLabelsButton from "../../../graphing/plotting/widgets/ToggleRunLabelsButton"
import KeyBody from "../../../graphing/plotting/GraphKeys/KeyBody"
import { moveGraphToNewLocation } from "../../../graphing/plotting/GraphKeys/utils"
import GraphingButtonToolbar from "../../../graphing/plotting/widgets/GraphingButtonToolbar"
import FontEditPopover from "../../../graphing/plotting/widgets/FontEditPopover"
import LabelingCompleteLabel from "../../../graphing/plotting/widgets/ToggleRunLabelsButton/LabelingCompleteLabel"
import LoadingIndicator from "../../../graphing/resources/LoadingIndicator"
import ZoomControlsPopover from "../../../graphing/zooming/ZoomControlsPopover"
import PerGraphZoomScrollButtons from "../../../graphing/zooming/ZoomControls/PerGraphZoomScrollButtons"
import { withGraphState } from "../../../config/GraphConfigProvider"
import { withPlotSettingsConfig } from "../../../config/PlotSettingsConfigProvider"
import DashLoader from "../../../widgets/DashLoader"
import {
  isLTPM,
  id_accessor,
  getDate,
  RunFilesDownloader,
  getFormattedMessage,
} from "../../../../components/shared_components_registration"
import { RunInfoPopover } from "../../../../components/shared_components_registration_overflow"

const registration = {
  refresh: (intl) => getFormattedMessage("general.refresh", "Refresh", true, intl),
  supp_graph_outline_color: isLTPM ? "var(--lanzalake-blue-primary)" : "var(--lanzalake-blue-mid)",
  last_upd_outline_color: isLTPM ? "var(--lanzalake-blue-primary)" : "var(--lanzalake-grey)",
  png_title: (site, activeGraph, run, exportBaseName) => {
    const base_name = activeGraph.title || activeGraph.label
    if (isLTPM) {
      return activeGraph.special === "multi"
        ? `${base_name}`
        : `${base_name}: ${exportBaseName} (${run.reactor})`
    } else {
      return activeGraph.special === "multi"
        ? `${site}: ${base_name}`
        : `${base_name}: ${site} ${run.reactor} ${exportBaseName}`
    }
  },
  show_last_updated_date: (run) => (isLTPM ? true : run.observation_max && run.reactor),
  show_last_updated_label: (run, intl) =>
    isLTPM
      ? `${run.name} ${intl.formatMessage({ id: "livedash.data.last.loaded" })}`
      : `${run.reactor} Observation Max`,
  remove_graph: (intl) => getFormattedMessage("graphing.remove.graph", "Remove Graph", true, intl),
  last_updated_value: (last_updated, run) =>
    isLTPM ? (
      getDate(last_updated)
    ) : (
      <span>
        {run.observation_max && getDate(run.observation_max)}
        &nbsp;
        {run.observation_max_day && <em>(day {run.observation_max_day.toFixed(2)})</em>}
      </span>
    ),
}

/* Component Specific Styles */
const localStyles = {
  "ltpm-tab-body": {
    margin: "15px",
    height: "100%",
  },
  "supplemental-live-graph": {
    cursor: "pointer",
    float: "left",
    margin: "3px",
    position: "relative",
    padding: "0",
  },
  "supplemental-selected": {
    border: `3px solid ${registration.supp_graph_outline_color}`,
    borderRadius: "5px",
    margin: "0",
  },
  "live-run-last-updated": {
    border: `1px solid ${registration.last_upd_outline_color}`,
    padding: "5px",
    margin: "0",
  },
}

export function RunErrorTabBody({ run, manualReloadRun, intl }) {
  return (
    <div style={{ ...localStyles["ltpm-tab-body"], height: "500px" }}>
      <div style={{ display: "flex", marginBottom: "10px" }}>
        <div className={"error-message"}>{run.err.toString()}</div>
        <ButtonGroup style={{ marginLeft: "auto", order: "2" }}>
          <Button
            variant={"outline-primary"}
            title={registration.refresh(intl)}
            onClick={(e) => {
              e.preventDefault()
              manualReloadRun(run)
            }}
          >
            <span className={"fa fa-refresh"} aria-hidden={"true"} />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

RunErrorTabBody.propTypes = {
  run: propTypes.object.isRequired,
  manualReloadRun: propTypes.func.isRequired,
  intl: propTypes.object,
}

export function RunLoadingTabBody() {
  return (
    <div style={{ ...localStyles["ltpm-tab-body"], height: "500px" }}>
      <DashLoader />
    </div>
  )
}

function _RunTabBody(props) {
  const {
    site,
    baton,
    x,
    supp_x,
    baseTime,
    live_graphs,
    xParams,
    run,
    activeRun,
    activeGraph,
    activeGraphIdx,
    availableRuns,
    timeExtractor, // From PlotSettingsConfig
    plotFactories,
    crossPlotProps,
    allObservationTypes,
    axesByPlot,
    maxTimeSpan,
    plotList,
    updateLiveGraphs,
    overlayRuns,
    setActiveGraph,
    intl,
    manualReloadRun,
    forceReloadPlotData,
    plotsPerRow,
    zoomConfig,
    selected_timezone,
    chartWidth,
    suppChartWidth,
    main_plot_settings,
    supplemental_plot_settings,
    chartWidthHeightChange,
    siteReactors = [],
    availableOnly,
  } = props
  const lastUpdated = run.loadedTime
  const [tz, setTZ] = useState(null)
  useEffect(() => {
    setTZ(selected_timezone)
  }, [selected_timezone])

  const { margin, height, smallWidthHeight, titleFontSize } = main_plot_settings
  const smallerConfig = chartWidth < chartWidthHeightChange
  const chartHeight = smallerConfig ? smallWidthHeight : height
  const suppChartHeight = smallerConfig
    ? supplemental_plot_settings.smallWidthHeight
    : supplemental_plot_settings.height
  const exportBaseName = run.name

  const [imageModal, setImageModal] = useState(false)
  const [dataModal, setDataModal] = useState(false)
  const [commentsModal, setCommentsModal] = useState(false)
  const [plotEntry, plotFactory] = getPlotEntryAndFactory(activeGraphIdx, plotFactories)

  // Plot Drag/Order Update Functionality
  const getGraphRowCol = (idx) => {
    // Get column and row location (in supplemental graph section) for given graph index
    const graph_ratio = idx / plotsPerRow
    const graph_row = Math.floor(graph_ratio)
    const remainder = graph_ratio - graph_row
    let graph_col = 0
    for (let col = 1; col <= plotsPerRow; col++) {
      if (col / plotsPerRow === remainder) {
        graph_col = col
      }
    }
    return [graph_row, graph_col + 1]
  }

  const updateGraphIdx = (old_idx, shifted_idx, plotIsActive) => {
    // Update plot index and update activeGraph/activeGraphIdx props as needed to match new graph order
    const new_index = shifted_idx > live_graphs.length - 1 ? live_graphs.length - 1 : shifted_idx

    if (new_index !== old_idx) {
      moveGraphToNewLocation(updateLiveGraphs, old_idx, new_index)

      if (plotIsActive) {
        // Need to update active plot state variables to match
        setActiveGraph(new_index, activeGraph)
      } else if (new_index <= activeGraphIdx && old_idx > activeGraphIdx) {
        // Shifting active plot index to match order shift
        setActiveGraph(activeGraphIdx + 1, activeGraph)
      } else if (old_idx < activeGraphIdx && new_index >= activeGraphIdx) {
        // Shifting active plot index to match order shift
        setActiveGraph(activeGraphIdx - 1, activeGraph)
      }
    }
  }

  const [startDragIndex, setStartDragIndex] = useState(null)
  const [dragIsActivePlot, setDragIsActivePlot] = useState(false)
  const suppSectionRef = useRef()
  const [suppBounds, setSuppBounds] = useState({})
  useEffect(() => {
    if (suppSectionRef.current) {
      // Figure out window locations of supp section rows and columns
      const graphRows = Math.ceil((live_graphs || []).length / plotsPerRow)
      const suppBounds = suppSectionRef.current.getBoundingClientRect()
      const verticalRowSpace =
        (suppBounds.bottom + window.scrollY - (suppBounds.top + window.scrollY)) / graphRows
      const horzColSpace =
        (suppBounds.right + window.scrollX - (suppBounds.left + window.scrollX)) / plotsPerRow
      const suppInfo = {}
      // Get row bounds
      let currTop = suppBounds.top + window.scrollY
      let rowBounds = []
      for (let row = 0; row < graphRows; row++) {
        const rowBtm = currTop + verticalRowSpace
        rowBounds.push({ top: currTop, bottom: rowBtm, row_idx: row })
        currTop = rowBtm
      }
      suppInfo["rows"] = rowBounds
      // Get col bounds
      let currLeft = suppBounds.left + window.scrollX
      let colBounds = []
      for (let col = 1; col <= plotsPerRow; col++) {
        const rowRight = currLeft + horzColSpace
        colBounds.push({ left: currLeft, right: rowRight, col_idx: col })
        currLeft = rowRight
      }
      suppInfo["cols"] = colBounds
      setSuppBounds(suppInfo)
    }
  }, [suppSectionRef.current, suppChartWidth])

  const dragUpdateGraphOrder = (e) => {
    // On drag: use col/row lookup to figure out what graph index should be
    e.stopPropagation()
    e.preventDefault()
    const cursorXLocation = e.clientX + window.scrollX
    const cursorYLocation = e.clientY + window.scrollY

    // Row cursor location match
    const row_num = suppBounds.rows.find(
      (row) => cursorYLocation < row.bottom && cursorYLocation >= row.top,
    )?.["row_idx"]
    // Col cursor location match
    const col_num = suppBounds.cols.find(
      (col) => cursorXLocation < col.right && cursorXLocation >= col.left,
    )?.["col_idx"]
    if (isNumber(col_num) && isNumber(row_num)) {
      const moveIndex = row_num * plotsPerRow + col_num - 1
      if (moveIndex !== startDragIndex) {
        //  Graph should get new index assignment
        updateGraphIdx(startDragIndex, moveIndex, dragIsActivePlot)
        setStartDragIndex(moveIndex)
      }
    }
  }
  // --- Plot Drag Functionality

  if (activeRun && activeRun[id_accessor] !== run[id_accessor]) {
    return <div style={localStyles["ltpm-tab-body"]} />
  } else if (!plotFactory || !baton) {
    return (
      <div style={localStyles["ltpm-tab-body"]}>
        <LoadingIndicator />
      </div>
    )
  } else {
    const [plotConfigUpdater, perGraphZoom, axesAndRanges, plotProps] = getPlotParams(
      activeGraphIdx,
      plotEntry,
      zoomConfig,
      run[id_accessor],
      activeGraph,
      axesByPlot,
      baseTime,
      timeExtractor,
      maxTimeSpan,
      baton,
    )

    const xAxisVariables = {
      baseTime: xParams.displayMin,
      minTime: xParams.minTime,
      maxTime: xParams.maxTime,
      maxFullTime: xParams.maxTimeSpan,
    }

    const sharedGraphComponents = {
      axesAndRanges: axesAndRanges,
      x: x,
      zoomRanges: perGraphZoom,
      zoomKey: run[id_accessor],
      width: chartWidth,
      height: chartHeight,
      margin: margin,
      timeExtractor: timeExtractor,
      graphKey: activeGraph,
      liveView: true,
      annotations: run.annotations || [],
      actions: run.actions || [],
      site_actions: run.site_actions || [],
      exportBaseName: exportBaseName,
      runInoculationDate: run?.inoculated_on,
    }

    const currentPlotCrossPlotProps = crossPlotProps[activeGraphIdx]

    const mainPlotGraphSettings = {
      ...sharedGraphComponents,
      runEventLabels: activeGraph.special === "multi" ? [] : run.runEventLabels || [],
    }

    const plot = getPlotComponent(
      plotFactory,
      baseTime,
      plotProps,
      plotConfigUpdater,
      currentPlotCrossPlotProps,
      titleFontSize,
      run,
      () => forceReloadPlotData(plotEntry),
      mainPlotGraphSettings,
    )

    const unifiedActions =
      activeGraph.special === "multi"
        ? currentPlotCrossPlotProps?.crossRunActions
        : run.getCombinedActions
          ? run.getCombinedActions(run)
          : []

    return (
      <div style={localStyles["ltpm-tab-body"]}>
        {imageModal && (
          <GraphImageModal
            intl={intl}
            onHide={() => setImageModal(false)}
            title={registration.png_title(site, activeGraph, run, exportBaseName)}
            plotEntry={plotEntry}
            observationTypes={allObservationTypes}
            graphIndex={activeGraphIdx}
            plotProps={plotProps}
            crossPlotProps={currentPlotCrossPlotProps}
            {...xAxisVariables}
            {...mainPlotGraphSettings}
          />
        )}
        {!!(dataModal || commentsModal) && (
          <DataAndCommentExportModals
            showDataModal={dataModal}
            showCommentsModal={commentsModal}
            onHideDataModal={() => setDataModal(false)}
            onHideCommentsModal={() => setCommentsModal(false)}
            actions={unifiedActions}
            perGraphZoom={perGraphZoom}
            commonDataVars={[
              activeGraph.special === "multi" ? availableRuns : run,
              timeExtractor,
              ...x.domain(),
            ]}
            plotFactory={plotFactory}
            exportBaseName={exportBaseName}
            op={activeGraph}
            viewerState={{ selected_timezone: tz, intl: intl }}
          />
        )}
        <div className={"row child-width-auto"}>
          {registration.show_last_updated_date(run) && (
            <div>
              <p style={localStyles["live-run-last-updated"]}>
                <label style={{ margin: "0px" }}>{`${registration.show_last_updated_label(
                  run,
                  intl,
                )}:`}</label>
                &nbsp;
                {registration.last_updated_value(lastUpdated, run)}
                &nbsp;
                <span>
                  <b
                    style={{
                      color: run?.finished_on ? "var(--lanzalake-red)" : "var(--lanzalake-green-primary)",
                      fontSize: "80%",
                    }}
                  >
                    {run?.finished_on
                      ? `(${getFormattedMessage("general.complete", "Complete", true, intl)} - ${getDate(run.finished_on, false, true)})`
                      : `(${getFormattedMessage("general.live", "Live", true, intl)})`}
                  </b>
                </span>
                <LabelingCompleteLabel run={activeRun} customStyle={{ fontSize: "80%" }} />
                &nbsp;
                <RunInfoPopover run={run} intl={intl} />
              </p>
            </div>
          )}
          <div style={{ marginLeft: "auto", display: "inline-flex" }}>
            <ButtonGroup style={{ marginLeft: "auto", order: "2" }}>
              <Button
                variant={"outline-primary"}
                title={registration.refresh(intl)}
                onClick={(e) => {
                  e.preventDefault()
                  manualReloadRun(run)
                }}
              >
                <span className={"fa fa-refresh"} aria-hidden={"true"} />
              </Button>
              <ZoomControlsPopover
                placement={"bottom"}
                intl={intl}
                axesAndRanges={axesAndRanges}
                zoomKey={run[id_accessor]}
                baseTime={baseTime}
                graphKey={activeGraph}
                graphIndex={activeGraphIdx}
                plotConfigUpdater={plotEntry.plotConfigUpdater}
                allowIndependentGraphZoom={true}
              />
              <RunFilesDownloader
                placement={"bottom"}
                button={true}
                exportBaseName={exportBaseName}
                fileList={activeRun.fileList}
              />
              <ExcelTravelerButton runData={run} intl={intl} isBtn={true} />
              <DownloadPopoverMenu
                placement={"bottom"}
                intl={intl}
                graph_name={setImageModal}
                data_name={setDataModal}
                comment_name={setCommentsModal}
                actions={unifiedActions}
                loading={availableRuns.filter((r) => r.loading)}
                op={activeGraph}
                showGraphModal={showModal}
                popover={false}
              />
              <ToggleAnnotationsButton
                annotations={run.annotations || []}
                placement={"bottom"}
                plotList={plotList}
                exportBaseName={`${site} ${run.reactor} ${exportBaseName}`}
                run={run}
                intl={intl}
              />
              <ToggleRunLabelsButton
                run={activeRun}
                exportBaseName={exportBaseName}
                op={activeGraph}
                plotImageSettings={xAxisVariables}
                PlotImage={(width, height, x) => {
                  const { runEventLabels, ...graphSettings } = mainPlotGraphSettings
                  return (
                    <>
                      <plotFactory.Component
                        {...graphSettings}
                        {...plotProps}
                        {...currentPlotCrossPlotProps}
                        x={x}
                        width={width}
                        height={height}
                        margin={{ top: 35, bottom: 80, left: 85, right: 90 }}
                        useSVG
                      />
                      <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
                        <GraphImageLegend
                          intl={intl}
                          graphKey={activeGraph}
                          graphIndex={activeGraphIdx}
                          plotEntry={plotEntry}
                          observationTypes={allObservationTypes}
                          crossPlotProps={currentPlotCrossPlotProps}
                          width={"90%"}
                        />
                      </div>
                    </>
                  )
                }}
              />
              <FontEditPopover intl={intl} />
              <GraphingButtonToolbar
                graphKey={activeGraph}
                plotEntry={plotEntry}
                intl={intl}
                index={activeGraphIdx}
                crossPlotProps={currentPlotCrossPlotProps}
                overlayRuns={overlayRuns}
                availableRuns={availableRuns}
                toggleAvailable={!perGraphZoom.y && !perGraphZoom.y2}
                timeExtractor={timeExtractor}
                axesAndRanges={axesAndRanges}
                baseTime={baseTime}
                small={false}
              />
              <Button
                title={registration.remove_graph(intl)}
                variant={"outline-primary"}
                disabled={live_graphs.length === 1}
                onClick={(e) => {
                  e.preventDefault()
                  updateLiveGraphs((live_graphs) => {
                    const graphs = [...live_graphs.filter((_, idx) => idx !== activeGraphIdx)]
                    setActiveGraph(0, graphs[0])
                    return graphs
                  })
                }}
              >
                <span className={"fa fa-minus-circle"} aria-hidden="true" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className={"row"}>
          <KeyBody
            intl={intl}
            graphKey={activeGraph}
            index={activeGraphIdx}
            plotEntry={plotEntry}
            graphs={live_graphs}
            updateGraphs={updateLiveGraphs}
            observationTypes={allObservationTypes}
            crossPlotProps={currentPlotCrossPlotProps}
            siteReactors={siteReactors}
            availableOnly={availableOnly}
            keyShowing
            liveView
          />
        </div>
        <PerGraphZoomScrollButtons
          intl={intl}
          axesAndRanges={axesAndRanges}
          zoomRanges={perGraphZoom}
          zoomConfig={zoomConfig}
        />
        <div style={{ marginTop: margin.top / 2 }}>{plot}</div>
        {/* Ref to get bounds of supp section for plot drag/drop */}
        <div className={"row child-width-auto"} ref={suppSectionRef}>
          {live_graphs.map((op, graph_idx) => {
            const { margin, titleFontSize } = supplemental_plot_settings
            const [plotEntry, plotFactory] = getPlotEntryAndFactory(graph_idx, plotFactories)
            const [plotConfigUpdater, perGraphZoom, axesAndRanges, plotProps] = getPlotParams(
              graph_idx,
              plotEntry,
              zoomConfig,
              run[id_accessor],
              op,
              axesByPlot,
              baseTime,
              timeExtractor,
              maxTimeSpan,
              baton,
            )
            const [graph_row, graph_col] = getGraphRowCol(graph_idx)
            const plot =
              !plotFactory || !baton
                ? ""
                : getPlotComponent(
                    plotFactory,
                    baseTime,
                    plotProps,
                    plotConfigUpdater,
                    crossPlotProps[graph_idx],
                    titleFontSize,
                    run,
                    () => forceReloadPlotData(plotEntry),
                    {
                      ...sharedGraphComponents,
                      axesAndRanges: axesAndRanges,
                      x: supp_x,
                      zoomRanges: perGraphZoom,
                      width: suppChartWidth,
                      height: suppChartHeight,
                      margin: margin,
                      graphKey: op,
                      avoid_annotations: true, // Don't pay attention to 'click' regarding adding new Annotations on supp plots
                      isSuppPlot: true, // Flag to indicate this is a supplemental plot (for font size scaling)
                      runEventLabels: op.special === "multi" ? [] : run.runEventLabels || [],
                    },
                  )

            const get_supplement_border = () => {
              return graph_idx === activeGraphIdx ? localStyles["supplemental-selected"] : {}
            }

            return (
              <div
                style={{
                  ...localStyles["supplemental-live-graph"],
                  ...get_supplement_border(),
                }}
                key={`supp_graph_${graph_idx}`}
                onClick={() => setActiveGraph(graph_idx, op)}
                draggable
                onDragStart={(_) => {
                  setStartDragIndex(graph_idx)
                  setDragIsActivePlot(graph_idx === activeGraphIdx)
                }}
                onDragOver={(e) => {
                  dragUpdateGraphOrder(e)
                }}
                onDragEnd={(e) => {
                  dragUpdateGraphOrder(e)
                  setStartDragIndex(null)
                  setDragIsActivePlot(false)
                }}
              >
                <Button
                  title={registration.remove_graph(intl)}
                  variant={"outline-primary"}
                  className={"btn-sm"}
                  style={{ position: "absolute", right: "0", zIndex: "100" }}
                  disabled={live_graphs.length === 1}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    updateLiveGraphs((live_graphs) => {
                      const graphs = [...live_graphs.filter((_, idx) => idx !== graph_idx)]
                      if (activeGraphIdx === graph_idx) {
                        setActiveGraph(0, graphs[0])
                      } else if (activeGraphIdx === live_graphs.length - 1) {
                        setActiveGraph(graphs.length - 1, graphs[graphs.length - 1])
                      } else if (graph_idx < activeGraphIdx) {
                        setActiveGraph(activeGraphIdx - 1, graphs[activeGraphIdx - 1])
                      }
                      return graphs
                    })
                  }}
                >
                  <span className={"fa fa-minus-circle"} aria-hidden="true" />
                </Button>
                <OverlayTrigger
                  trigger={"click"}
                  placement={"bottom"}
                  rootClose
                  overlay={
                    <Popover>
                      <Popover.Body>
                        <div style={{ display: "flex" }} onClick={(e) => e.stopPropagation()}>
                          <label style={{ margin: "0" }}>r:</label>&nbsp;
                          <select
                            name={"graph_row"}
                            style={{ fontSize: "75%", padding: "0", borderRadius: "3px" }}
                            value={graph_row}
                            onChange={(e) => {
                              e.target.value !== graph_row &&
                                updateGraphIdx(
                                  graph_idx,
                                  e.target.value * plotsPerRow + (graph_col - 1),
                                  graph_idx === activeGraphIdx,
                                )
                              document.body.click() // Trigger a popover close
                            }}
                          >
                            {(suppBounds?.rows || []).map((item, idx) => {
                              return (
                                <option key={idx} value={item.row_idx}>
                                  {item.row_idx + 1}
                                </option>
                              )
                            })}
                          </select>
                        </div>
                        <div style={{ display: "flex" }} onClick={(e) => e.stopPropagation()}>
                          <label style={{ margin: "0" }}>c:</label>&nbsp;
                          <select
                            name={"graph_col"}
                            style={{ fontSize: "75%", padding: "0", borderRadius: "3px" }}
                            value={graph_col}
                            onChange={(e) => {
                              e.target.value !== graph_col &&
                                updateGraphIdx(
                                  graph_idx,
                                  graph_row * plotsPerRow + (e.target.value - 1),
                                  graph_idx === activeGraphIdx,
                                )
                              document.body.click() // Trigger a popover close
                            }}
                          >
                            {(suppBounds?.cols || []).map((item, idx) => {
                              return (
                                <option key={idx} value={item.col_idx}>
                                  {item.col_idx}
                                </option>
                              )
                            })}
                          </select>
                        </div>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button
                    variant={"outline-primary"}
                    className={"btn-sm"}
                    style={{
                      position: "absolute",
                      left: "0",
                      zIndex: "100",
                    }}
                    disabled={!isNumber(graph_col) || !isNumber(graph_row)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className={"fa fa-hand-rock-o"} aria-hidden="true" />
                  </Button>
                </OverlayTrigger>
                {plot}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

export const RunTabBody = withPlotSettingsConfig(withGraphState(_RunTabBody))

RunTabBody.propTypes = {
  site: propTypes.string, // unused in LTPM
  run: propTypes.object.isRequired,
  availableRuns: propTypes.array.isRequired,
  activeRun: propTypes.object.isRequired,
  activeGraph: propTypes.object.isRequired,
  activeGraphIdx: propTypes.number,
  plotList: propTypes.array.isRequired,
  plotFactories: propTypes.array.isRequired,
  baton: propTypes.object,
  x: propTypes.func,
  supp_x: propTypes.func,
  baseTime: propTypes.oneOfType([propTypes.number, propTypes.object]),
  live_graphs: propTypes.array, // From withGraphState
  show_site_actions: propTypes.bool, // From withGraphState
  show_actions: propTypes.bool, // From withGraphState
  show_critical_actions: propTypes.bool, // From withGraphState
  show_annotations: propTypes.bool, // From withGraphState
  xParams: propTypes.object,
  timeExtractor: propTypes.object, // From PlotSettingsConfigProvider
  crossPlotProps: propTypes.array,
  allObservationTypes: propTypes.object,
  axesByPlot: propTypes.array,
  maxTimeSpan: propTypes.number,
  updateLiveGraphs: propTypes.func,
  overlayRuns: propTypes.func,
  setActiveGraph: propTypes.func,
  // number in LTPM, object in LL
  last_updated: propTypes.oneOfType([propTypes.number, propTypes.object]),
  manualReloadRun: propTypes.func,
  forceReloadPlotData: propTypes.func,
  plotsPerRow: propTypes.number,
  zoomConfig: propTypes.object,
  selected_timezone: propTypes.string,
  chartWidth: propTypes.number,
  suppChartWidth: propTypes.number,
  chartWidthHeightChange: propTypes.number,
  main_plot_settings: propTypes.object,
  supplemental_plot_settings: propTypes.object,
  intl: propTypes.object,
  siteReactors: propTypes.array,
}

const getPlotEntryAndFactory = (graph_index, plotFactories) => {
  const plotEntry = plotFactories[graph_index]
  const plotFactory = plotEntry ? plotEntry.plotFactory : null
  return [plotEntry, plotFactory]
}

const getPlotParams = (
  graph_index,
  plotEntry,
  zoomConfig,
  run_id,
  op,
  axesByPlot,
  baseTime,
  timeExtractor,
  maxTimeSpan,
  baton,
) => {
  const perGraphZoom = zoomConfig.zoomForPlot(run_id, op)
  const axesAndRanges = Object.assign({}, axesByPlot[graph_index])
  if (axesAndRanges.time) {
    axesAndRanges.time = [baseTime, timeExtractor.offsetDays(baseTime, maxTimeSpan)]
  }
  const plotProps = baton && plotEntry ? baton.plotPropsByKey[plotEntry.plotKey] : {}
  const plotConfigUpdater = plotEntry ? plotEntry.plotConfigUpdater : null
  return [plotConfigUpdater, perGraphZoom, axesAndRanges, plotProps]
}

const getPlotComponent = (
  plotFactory,
  baseTime,
  plotProps,
  plotConfigUpdater,
  crossPlotProps,
  titleFontSize,
  run,
  forceReloadPlotData,
  sharedGraphComponents,
) => {
  return (
    <plotFactory.Component
      baseTime={baseTime}
      titleFontSize={titleFontSize}
      graphTitleSupplement={
        sharedGraphComponents.graphKey["special"] !== "multi"
          ? run.site
            ? `${run.site} - ${run.reactor}`
            : `${run.reactor}`
          : null
      }
      forceReloadPlotData={forceReloadPlotData}
      {...sharedGraphComponents}
      {...plotProps}
      {...crossPlotProps}
    />
  )
}
